import { useCallback, useState } from 'react';

import { checkEmail, checkListName } from '@psy/validations';

import { useLanguage } from '../../contexts/language';
import { useField, validateAll } from '../../hooks/field';
import { useInvitationChanges } from '../../hooks/invitation-changes';

import { Button } from '../button';
import { NextSteps } from '../next-steps';
import { TextField } from '../text-field';

import styles from './style.module.css';

// The invitation form: the patient's address, checked with the rule sign-in
// uses, and an optional name for the clinician's own list. Sending says
// where it went and empties the form for the next one; a refusal, such as an
// address already invited, goes under the address.
export function InviteForm({ onSent }) {
  const { text, translate } = useLanguage();
  const { invite } = useInvitationChanges();
  const email = useField({ initial: '', check: checkEmail });
  const name = useField({ initial: '', check: checkListName });
  const [ sending, setSending ] = useState(false);
  const [ sentTo, setSentTo ] = useState();

  const onSubmit = useCallback(function(event) {
    event.preventDefault();

    if (validateAll([ email, name ]) === false) {
      return null;
    }

    setSending(true);
    setSentTo();

    return invite({ email: email.value, name: name.value }).then(function(invitation) {
      setSending(false);
      setSentTo(invitation.email);
      email.reset();
      name.reset();

      // The invitation is sent whether or not the list can be read again
      // right now; a list that failed to refresh keeps what it showed and
      // is read again on the next visit.
      return onSent().catch(function() {
        return null;
      });
    }).catch(function(failed) {
      setSending(false);
      email.refuse(failed.message);
    });
  }, [ email, name, invite, onSent ]);

  let confirmation = null;

  if (sentTo != null) {
    confirmation = <p className={ styles.sent } role='status'>{ text.invitationSentTo.replace('{email}', sentTo) }</p>;
  }

  return (
    <form className={ styles.form } onSubmit={ onSubmit } noValidate>
      <TextField
        ref={ email.ref }
        id='invite-email'
        label={ text.patientEmail }
        type='email'
        value={ email.value }
        onChange={ email.onChange }
        onBlur={ email.onBlur }
        error={ translate(email.error) }
        autoComplete='off'
        placeholder='name@example.com'
      />

      <TextField
        ref={ name.ref }
        id='invite-name'
        label={ text.nameForList }
        value={ name.value }
        onChange={ name.onChange }
        onBlur={ name.onBlur }
        error={ translate(name.error) }
        hint={ text.nameForListHint }
        autoComplete='off'
      />

      <NextSteps title={ text.whatHappensNext } steps={ [ text.inviteStep1, text.inviteStep2, text.inviteStep3 ] }/>

      <Button type='submit' disabled={ sending }>{ text.sendInvitation }</Button>

      { confirmation }
    </form>
  );
}
